'use client';

/**
 * Billing Integration Component
 *
 * Manages billing records for a service referral:
 * - Create billing records with CPT/HCPCS codes
 * - Track billing status (pending, submitted, paid, denied)
 * - Display billing summary
 *
 * Used in case workspace and provider billing workflows.
 */

import React, { useState, useEffect } from 'react';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { useFormattedDate } from '../../hooks/useUserTimezone';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  DollarSign,
  Plus,
  FileText,
  Calendar,
  Clock,
  AlertCircle,
  CheckCircle,
  XCircle,
  Loader2,
  ChevronDown,
  ChevronUp,
  Send,
  CreditCard,
} from 'lucide-react';
import {
  serviceReferralsAPI,
  BillingRecord,
  BillingRecordCreate,
  BillingSummary,
  BillingContext,
  BillingStatus,
  CodeSystem,
  getBillingStatusLabel,
} from '@/services/api/serviceReferrals';
import { Separator } from '@/components/ui/separator';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

interface BillingIntegrationProps {
  referralId: number;
  onBillingCreated?: (billing: BillingRecord) => void;
  onStatusChange?: () => void;
  readOnly?: boolean;
  compact?: boolean;
  showContext?: boolean; // Show accepted pricing and consent context
  className?: string;
}

// ============================================================================
// CONSTANTS
// ============================================================================

// Labels will be translated at render time using t()
const BILLING_STATUS_CONFIG: Record<BillingStatus, { labelKey: string; defaultLabel: string; color: string; icon: React.ReactNode }> = {
  pending: { labelKey: 'serviceReferrals.billing.status.pending', defaultLabel: 'Pending', color: 'gray', icon: <Clock className="w-4 h-4" /> },
  submitted: { labelKey: 'serviceReferrals.billing.status.submitted', defaultLabel: 'Submitted', color: 'blue', icon: <Send className="w-4 h-4" /> },
  paid: { labelKey: 'serviceReferrals.billing.status.paid', defaultLabel: 'Paid', color: 'green', icon: <CheckCircle className="w-4 h-4" /> },
  partial_paid: { labelKey: 'serviceReferrals.billing.status.partialPaid', defaultLabel: 'Partial', color: 'amber', icon: <DollarSign className="w-4 h-4" /> },
  denied: { labelKey: 'serviceReferrals.billing.status.denied', defaultLabel: 'Denied', color: 'red', icon: <XCircle className="w-4 h-4" /> },
  appealed: { labelKey: 'serviceReferrals.billing.status.appealed', defaultLabel: 'Appealed', color: 'purple', icon: <AlertCircle className="w-4 h-4" /> },
};

const CODE_SYSTEMS: { value: CodeSystem; label: string }[] = [
  { value: 'CPT', label: 'CPT' },
  { value: 'HCPCS', label: 'HCPCS' },
  { value: 'custom', label: 'Custom' },
];

// Common billing codes for quick selection
const COMMON_BILLING_CODES = [
  { code: '99214', system: 'CPT', description: 'Office visit, established patient, moderate' },
  { code: '99215', system: 'CPT', description: 'Office visit, established patient, high' },
  { code: '99407', system: 'CPT', description: 'Smoking cessation counseling, 3-10 min' },
  { code: '96156', system: 'CPT', description: 'Health behavior assessment' },
  { code: '96158', system: 'CPT', description: 'Health behavior intervention, individual' },
  { code: 'H0004', system: 'HCPCS', description: 'Behavioral health counseling' },
  { code: 'H0023', system: 'HCPCS', description: 'Behavioral health outreach' },
  { code: 'H2015', system: 'HCPCS', description: 'Comprehensive community support' },
  { code: 'H2016', system: 'HCPCS', description: 'Comprehensive community support, per diem' },
  { code: 'T1016', system: 'HCPCS', description: 'Case management, each 15 minutes' },
  { code: 'T1017', system: 'HCPCS', description: 'Targeted case management' },
  { code: 'S5110', system: 'HCPCS', description: 'Home care training, family member' },
];

// ============================================================================
// COMPONENT
// ============================================================================

export function BillingIntegration({
  referralId,
  onBillingCreated,
  onStatusChange,
  readOnly = false,
  compact = false,
  showContext = true,
  className = '',
}: BillingIntegrationProps) {
  const { t } = useAppTranslation();
  const { fmt } = useFormattedDate();
  // State
  const [billingRecords, setBillingRecords] = useState<BillingRecord[]>([]);
  const [summary, setSummary] = useState<BillingSummary | null>(null);
  const [billingContext, setBillingContext] = useState<BillingContext | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showPaymentOptions, setShowPaymentOptions] = useState(false);
  const [selectedPaymentModel, setSelectedPaymentModel] = useState<string>('invoice');
  const [expandedRecordId, setExpandedRecordId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Form State
  const [formData, setFormData] = useState<BillingRecordCreate>({
    billing_code: '',
    code_system: 'CPT',
    modifier_codes: [],
    diagnosis_codes: [],
    place_of_service: '11', // Office
    units: 1,
    rate_per_unit: 0,
    service_date: new Date().toISOString().split('T')[0],
    prior_auth_number: '',
    prior_auth_required: false,
    notes: '',
  });

  // Load data
  useEffect(() => {
    loadData();
  }, [referralId]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [records, summaryData, contextData] = await Promise.all([
        serviceReferralsAPI.getBillingRecords(referralId),
        serviceReferralsAPI.getBillingSummary(referralId),
        showContext ? serviceReferralsAPI.getBillingContext(referralId).catch((err) => {
          console.error('Failed to load billing context:', err);
          return null;
        }) : Promise.resolve(null),
      ]);
      setBillingRecords(records);
      setSummary(summaryData);
      if (contextData) {
        setBillingContext(contextData);
        // Pre-fill form with accepted pricing or estimated pricing
        if (contextData.accepted_pricing) {
          setFormData(prev => ({
            ...prev,
            billing_code: contextData.accepted_pricing?.billing_code || 'SVC001',
            rate_per_unit: contextData.accepted_pricing?.rate || 0,
            units: contextData.accepted_pricing?.units || 1,
          }));
        } else if (contextData.pricing) {
          // Fallback to estimated pricing from referral
          setFormData(prev => ({
            ...prev,
            billing_code: 'SVC001',
            rate_per_unit: contextData.pricing.estimated_rate || 0,
            units: contextData.pricing.estimated_units || 1,
          }));
        }
      } else if (showContext) {
        // If billing context failed but we expected it, show a default context
        // This allows manual billing even if context endpoint has issues
        setBillingContext({
          referral_id: referralId,
          status: 'service_completed',
          service_name: null,
          service_type_code: null,
          employee: null,
          employer: null,
          service_delivery: null,
          pricing: null,
          accepted_pricing: null,
          consent_status: null,
          milestones: [],
          billing_ready: true, // Allow billing workflow to show
          requires_consent_confirmation: false,
          payment_models: [
            { id: 'invoice', name: 'Invoice Billing', description: 'Submit invoice for employer approval', available: true },
            { id: 'auto_ach', name: 'ACH Payment', description: 'Automatic ACH transfer', available: true },
            { id: 'prepaid_card', name: 'Prepaid Card', description: 'Issue prepaid card to provider', available: true },
          ],
        } as BillingContext);
      }
    } catch (err) {
      console.error('Failed to load billing data:', err);
      setError('Failed to load billing records');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmDelivery = async () => {
    setProcessing(true);
    setError(null);
    try {
      const result = await serviceReferralsAPI.confirmServiceDelivery(referralId);
      setSuccessMessage(result.message);
      await loadData();
      onStatusChange?.();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to confirm delivery');
    } finally {
      setProcessing(false);
    }
  };

  const handleProcessBilling = async () => {
    setProcessing(true);
    setError(null);
    try {
      const result = await serviceReferralsAPI.processBilling(
        referralId,
        selectedPaymentModel as 'invoice' | 'auto_ach' | 'prepaid_card'
      );

      // Build detailed success message based on payment method
      let message = result.message;
      if (result.next_step) {
        message += `. ${result.next_step}`;
      }
      if (result.payment_status) {
        message += ` (Status: ${result.payment_status})`;
      }
      if (result.card_last4) {
        message += ` Card ending in ${result.card_last4}`;
      }

      setSuccessMessage(message);
      setShowPaymentOptions(false);
      await loadData();
      onStatusChange?.();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to process billing');
    } finally {
      setProcessing(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.billing_code.trim()) {
      setError('Billing code is required');
      return;
    }
    if (formData.rate_per_unit <= 0) {
      setError('Rate per unit must be greater than 0');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const newRecord = await serviceReferralsAPI.createBillingRecord(referralId, formData);
      setBillingRecords([newRecord, ...billingRecords]);
      // Reload summary
      const newSummary = await serviceReferralsAPI.getBillingSummary(referralId);
      setSummary(newSummary);
      setShowAddForm(false);
      resetForm();
      onBillingCreated?.(newRecord);
    } catch (err) {
      console.error('Failed to create billing record:', err);
      setError('Failed to create billing record');
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setFormData({
      billing_code: '',
      code_system: 'CPT',
      modifier_codes: [],
      diagnosis_codes: [],
      place_of_service: '11',
      units: 1,
      rate_per_unit: 0,
      service_date: new Date().toISOString().split('T')[0],
      prior_auth_number: '',
      prior_auth_required: false,
      notes: '',
    });
  };

  const handleQuickCodeSelect = (code: { code: string; system: string; description: string }) => {
    setFormData({
      ...formData,
      billing_code: code.code,
      code_system: code.system as CodeSystem,
    });
  };

  const formatCurrency = (amount?: number) => {
    if (amount === undefined || amount === null) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    return fmt.medium(dateStr);
  };

  const renderStatusBadge = (status: BillingStatus) => {
    const config = BILLING_STATUS_CONFIG[status];
    return (
      <Badge
        variant="outline"
        className={`
          ${config.color === 'gray' ? 'border-gray-300 text-gray-600 bg-gray-50' : ''}
          ${config.color === 'blue' ? 'border-blue-300 text-blue-700 bg-blue-50' : ''}
          ${config.color === 'green' ? 'border-green-300 text-green-700 bg-green-50' : ''}
          ${config.color === 'amber' ? 'border-amber-300 text-amber-700 bg-amber-50' : ''}
          ${config.color === 'red' ? 'border-red-300 text-red-700 bg-red-50' : ''}
          ${config.color === 'purple' ? 'border-purple-300 text-purple-700 bg-purple-50' : ''}
        `}
      >
        {config.icon}
        <span className="ml-1">{t(config.labelKey, config.defaultLabel)}</span>
      </Badge>
    );
  };

  // Compact view for sidebars
  if (compact) {
    return (
      <Card className={className}>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <CreditCard className="w-4 h-4" />
            {t('serviceReferrals.billing.title', 'Billing')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <div className="space-y-2 text-sm">
              {/* Accepted Pricing */}
              {billingContext?.accepted_pricing && (
                <div className="p-2 bg-green-50 rounded border border-green-200 mb-2">
                  <div className="flex justify-between">
                    <span className="text-green-700 text-xs font-medium">{t('serviceReferrals.billing.acceptedRate', 'Accepted Rate')}</span>
                    <span className="font-bold text-green-800">
                      {formatCurrency(billingContext.accepted_pricing.rate || 0)}
                    </span>
                  </div>
                </div>
              )}

              {/* Summary */}
              {summary ? (
                <>
                  <div className="flex justify-between">
                    <span className="text-gray-500">{t('serviceReferrals.billing.totalBilled', 'Total Billed')}:</span>
                    <span className="font-medium">{formatCurrency(summary.total_billed)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">{t('serviceReferrals.billing.paid', 'Paid')}:</span>
                    <span className="font-medium text-green-600">{formatCurrency(summary.total_paid)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">{t('serviceReferrals.billing.balance', 'Balance')}:</span>
                    <span className="font-medium text-amber-600">{formatCurrency(summary.balance_due)}</span>
                  </div>
                </>
              ) : (
                <p className="text-xs text-gray-500">{t('serviceReferrals.billing.noBillingRecords', 'No billing records')}</p>
              )}

              {/* Billing Status */}
              {billingContext && !billingContext.billing_ready && billingContext.status === 'service_completed' && (
                <div className="pt-2 border-t">
                  <p className="text-xs text-amber-600">{t('serviceReferrals.billing.awaitingDeliveryConfirmation', 'Awaiting delivery confirmation')}</p>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  // ========================================================================
  // FULL RENDER
  // ========================================================================

  return (
    <Card className={className}>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              <CreditCard className="w-5 h-5" />
              {t('serviceReferrals.billing.serviceBilling', 'Service Billing')}
            </CardTitle>
            <CardDescription>
              {t('serviceReferrals.billing.manageBillingRecords', 'Manage billing records for this service')}
            </CardDescription>
          </div>
          {!readOnly && (
            <Button
              onClick={() => setShowAddForm(!showAddForm)}
              variant={showAddForm ? 'outline' : 'default'}
              size="sm"
            >
              <Plus className="w-4 h-4 mr-1" />
              {showAddForm ? t('common.cancel', 'Cancel') : t('serviceReferrals.billing.addBilling', 'Add Billing')}
            </Button>
          )}
        </div>

        {/* Summary Stats */}
        {summary && (
          <div className="mt-4 grid grid-cols-4 gap-3">
            <div className="p-3 bg-gray-50 rounded-lg text-center">
              <div className="text-xl font-bold">{formatCurrency(summary.total_billed)}</div>
              <div className="text-xs text-gray-500">{t('serviceReferrals.billing.totalBilled', 'Total Billed')}</div>
            </div>
            <div className="p-3 bg-green-50 rounded-lg text-center">
              <div className="text-xl font-bold text-green-700">{formatCurrency(summary.total_paid)}</div>
              <div className="text-xs text-green-600">{t('serviceReferrals.billing.paid', 'Paid')}</div>
            </div>
            <div className="p-3 bg-blue-50 rounded-lg text-center">
              <div className="text-xl font-bold text-blue-700">{formatCurrency(summary.total_adjusted)}</div>
              <div className="text-xs text-blue-600">{t('serviceReferrals.billing.adjusted', 'Adjusted')}</div>
            </div>
            <div className="p-3 bg-amber-50 rounded-lg text-center">
              <div className="text-xl font-bold text-amber-700">{formatCurrency(summary.balance_due)}</div>
              <div className="text-xs text-amber-600">{t('serviceReferrals.billing.balanceDue', 'Balance Due')}</div>
            </div>
          </div>
        )}
      </CardHeader>

      <CardContent>
        {/* Success Message */}
        {successMessage && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md text-green-700 text-sm flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4" />
              {successMessage}
            </div>
            <Button variant="ghost" size="sm" onClick={() => setSuccessMessage(null)}>
              <XCircle className="w-4 h-4" />
            </Button>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
            {error}
          </div>
        )}

        {/* Payment Processing Section - Always show when there's a balance due */}
        {summary && summary.balance_due > 0 && !readOnly && (
          <div className="mb-6">
            {!showPaymentOptions ? (
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium text-blue-800 flex items-center gap-2">
                      <CreditCard className="w-4 h-4" />
                      {t('serviceReferrals.billing.processPayment', 'Process Payment')}
                    </h4>
                    <p className="text-sm text-blue-700 mt-1">
                      {t('serviceReferrals.billing.balanceDue', 'Balance due')}: <span className="font-bold">{formatCurrency(summary.balance_due)}</span>
                    </p>
                  </div>
                  <Button onClick={() => setShowPaymentOptions(true)}>
                    {t('serviceReferrals.billing.selectPaymentMethod', 'Select Payment Method')}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="p-4 bg-gray-50 border rounded-lg space-y-4">
                <h4 className="font-medium text-gray-800">{t('serviceReferrals.billing.selectPaymentMethod', 'Select Payment Method')}</h4>
                <p className="text-sm text-gray-600">
                  {t('serviceReferrals.billing.processingPaymentOf', 'Processing payment of')} <span className="font-bold">{formatCurrency(summary.balance_due)}</span>
                </p>
                <div className="space-y-2">
                  <label
                    className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                      selectedPaymentModel === 'invoice'
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:bg-gray-100'
                    }`}
                  >
                    <input
                      type="radio"
                      name="payment_model_main"
                      value="invoice"
                      checked={selectedPaymentModel === 'invoice'}
                      onChange={(e) => setSelectedPaymentModel(e.target.value)}
                      className="mt-1"
                    />
                    <div>
                      <div className="font-medium">{t('serviceReferrals.billing.invoiceBilling', 'Invoice Billing')}</div>
                      <div className="text-sm text-gray-500">{t('serviceReferrals.billing.invoiceBillingDesc', 'Submit invoice for employer review and approval')}</div>
                    </div>
                  </label>
                  <label
                    className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                      selectedPaymentModel === 'auto_ach'
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:bg-gray-100'
                    }`}
                  >
                    <input
                      type="radio"
                      name="payment_model_main"
                      value="auto_ach"
                      checked={selectedPaymentModel === 'auto_ach'}
                      onChange={(e) => setSelectedPaymentModel(e.target.value)}
                      className="mt-1"
                    />
                    <div>
                      <div className="font-medium">{t('serviceReferrals.billing.achPayment', 'ACH Payment')}</div>
                      <div className="text-sm text-gray-500">{t('serviceReferrals.billing.achPaymentDesc', 'Automatic bank transfer (2-3 business days)')}</div>
                    </div>
                  </label>
                  <label
                    className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                      selectedPaymentModel === 'prepaid_card'
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:bg-gray-100'
                    }`}
                  >
                    <input
                      type="radio"
                      name="payment_model_main"
                      value="prepaid_card"
                      checked={selectedPaymentModel === 'prepaid_card'}
                      onChange={(e) => setSelectedPaymentModel(e.target.value)}
                      className="mt-1"
                    />
                    <div>
                      <div className="font-medium">{t('serviceReferrals.billing.prepaidCard', 'Prepaid Card')}</div>
                      <div className="text-sm text-gray-500">{t('serviceReferrals.billing.prepaidCardDesc', 'Issue prepaid card to provider with payment amount')}</div>
                    </div>
                  </label>
                </div>
                <div className="flex gap-2 justify-end">
                  <Button variant="outline" onClick={() => setShowPaymentOptions(false)}>
                    {t('common.cancel', 'Cancel')}
                  </Button>
                  <Button onClick={handleProcessBilling} disabled={processing}>
                    {processing && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    {t('serviceReferrals.billing.process', 'Process')} {formatCurrency(summary.balance_due)}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Billing Context - Accepted Pricing and Consent */}
        {billingContext && showContext && (
          <div className="mb-6 space-y-4">
            {/* Accepted Pricing */}
            {billingContext.accepted_pricing && (
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-medium text-green-800 flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" />
                    {t('serviceReferrals.billing.acceptedPricing', 'Accepted Pricing')}
                  </h4>
                  <Badge variant="outline" className="bg-green-100 text-green-800">
                    {billingContext.accepted_pricing.status}
                  </Badge>
                </div>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="text-green-600">{t('serviceReferrals.billing.rate', 'Rate')}:</span>{' '}
                    <span className="font-medium">{formatCurrency(billingContext.accepted_pricing.rate || 0)}</span>
                  </div>
                  <div>
                    <span className="text-green-600">{t('serviceReferrals.billing.units', 'Units')}:</span>{' '}
                    <span className="font-medium">{billingContext.accepted_pricing.units}</span>
                  </div>
                  <div>
                    <span className="text-green-600">{t('serviceReferrals.billing.total', 'Total')}:</span>{' '}
                    <span className="font-bold">{formatCurrency(billingContext.accepted_pricing.total_amount || 0)}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Consent Status */}
            {billingContext.consent_status && (
              <div className={`p-4 rounded-lg border ${
                billingContext.consent_status.is_valid
                  ? 'bg-blue-50 border-blue-200'
                  : 'bg-amber-50 border-amber-200'
              }`}>
                <div className="flex items-center justify-between">
                  <h4 className={`font-medium flex items-center gap-2 ${
                    billingContext.consent_status.is_valid ? 'text-blue-800' : 'text-amber-800'
                  }`}>
                    {billingContext.consent_status.is_valid ? (
                      <CheckCircle className="w-4 h-4" />
                    ) : (
                      <AlertCircle className="w-4 h-4" />
                    )}
                    {t('serviceReferrals.billing.employeeConsent', 'Employee Consent')}
                  </h4>
                  <Badge variant="outline" className={
                    billingContext.consent_status.is_valid
                      ? 'bg-blue-100 text-blue-800'
                      : 'bg-amber-100 text-amber-800'
                  }>
                    {billingContext.consent_status.status}
                  </Badge>
                </div>
                {!billingContext.consent_status.is_valid && (
                  <p className="mt-2 text-sm text-amber-700">
                    {t('serviceReferrals.billing.consentReVerify', 'Consent may need to be re-verified before billing.')}
                  </p>
                )}
              </div>
            )}

            {/* Service Delivery Confirmation - Only show if not billing ready */}
            {billingContext.status === 'service_completed' && !billingContext.billing_ready && !readOnly && (
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium text-amber-800 flex items-center gap-2">
                      <AlertCircle className="w-4 h-4" />
                      {t('serviceReferrals.billing.confirmServiceDelivery', 'Confirm Service Delivery')}
                    </h4>
                    <p className="text-sm text-amber-700 mt-1">
                      {t('serviceReferrals.billing.confirmDeliveryDesc', 'Please confirm that the service was delivered as agreed before processing billing.')}
                    </p>
                  </div>
                  <Button
                    onClick={handleConfirmDelivery}
                    disabled={processing}
                    className="bg-amber-600 hover:bg-amber-700"
                  >
                    {processing && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    {t('serviceReferrals.billing.confirmDelivery', 'Confirm Delivery')}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        <Separator className="my-4" />

        {/* Add Billing Form */}
        {showAddForm && (
          <form onSubmit={handleSubmit} className="mb-6 p-4 border rounded-lg bg-gray-50 space-y-4">
            {/* Quick Code Selection */}
            <div>
              <Label>{t('serviceReferrals.billing.quickSelectCode', 'Quick Select Code')}</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {COMMON_BILLING_CODES.slice(0, 6).map((code) => (
                  <Button
                    key={code.code}
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => handleQuickCodeSelect(code)}
                    className={formData.billing_code === code.code ? 'border-blue-500 bg-blue-50' : ''}
                  >
                    {code.code}
                  </Button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              {/* Billing Code */}
              <div>
                <Label htmlFor="billing_code">{t('serviceReferrals.billing.billingCode', 'Billing Code')} *</Label>
                <Input
                  id="billing_code"
                  value={formData.billing_code}
                  onChange={(e) => setFormData({ ...formData, billing_code: e.target.value })}
                  placeholder="e.g., 99214"
                  required
                />
              </div>

              {/* Code System */}
              <div>
                <Label htmlFor="code_system">{t('serviceReferrals.billing.codeSystem', 'Code System')}</Label>
                <select
                  id="code_system"
                  value={formData.code_system}
                  onChange={(e) => setFormData({ ...formData, code_system: e.target.value as CodeSystem })}
                  className="w-full px-3 py-2 border rounded-md"
                >
                  {CODE_SYSTEMS.map((sys) => (
                    <option key={sys.value} value={sys.value}>{sys.label}</option>
                  ))}
                </select>
              </div>

              {/* Service Date */}
              <div>
                <Label htmlFor="service_date">{t('serviceReferrals.billing.serviceDate', 'Service Date')} *</Label>
                <Input
                  id="service_date"
                  type="date"
                  value={formData.service_date}
                  onChange={(e) => setFormData({ ...formData, service_date: e.target.value })}
                  required
                />
              </div>

              {/* Units */}
              <div>
                <Label htmlFor="units">{t('serviceReferrals.billing.units', 'Units')} *</Label>
                <Input
                  id="units"
                  type="number"
                  min={1}
                  value={formData.units}
                  onChange={(e) => setFormData({ ...formData, units: parseInt(e.target.value) || 1 })}
                  required
                />
              </div>

              {/* Rate per Unit */}
              <div>
                <Label htmlFor="rate_per_unit">{t('serviceReferrals.billing.ratePerUnit', 'Rate per Unit ($)')} *</Label>
                <Input
                  id="rate_per_unit"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.rate_per_unit}
                  onChange={(e) => setFormData({ ...formData, rate_per_unit: parseFloat(e.target.value) || 0 })}
                  required
                />
              </div>

              {/* Total (calculated) */}
              <div>
                <Label>{t('serviceReferrals.billing.totalAmount', 'Total Amount')}</Label>
                <div className="px-3 py-2 border rounded-md bg-gray-100 font-medium">
                  {formatCurrency((formData.units || 0) * (formData.rate_per_unit || 0))}
                </div>
              </div>

              {/* Place of Service */}
              <div>
                <Label htmlFor="place_of_service">{t('serviceReferrals.billing.placeOfService', 'Place of Service')}</Label>
                <select
                  id="place_of_service"
                  value={formData.place_of_service}
                  onChange={(e) => setFormData({ ...formData, place_of_service: e.target.value })}
                  className="w-full px-3 py-2 border rounded-md"
                >
                  <option value="11">11 - Office</option>
                  <option value="12">12 - Home</option>
                  <option value="02">02 - Telehealth</option>
                  <option value="99">99 - Other</option>
                </select>
              </div>

              {/* Prior Auth */}
              <div className="col-span-2">
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="prior_auth_required"
                      checked={formData.prior_auth_required}
                      onChange={(e) => setFormData({ ...formData, prior_auth_required: e.target.checked })}
                    />
                    <Label htmlFor="prior_auth_required" className="cursor-pointer">
                      {t('serviceReferrals.billing.priorAuthRequired', 'Prior Auth Required')}
                    </Label>
                  </div>
                  {formData.prior_auth_required && (
                    <Input
                      placeholder={t('serviceReferrals.billing.priorAuthNumber', 'Prior Auth Number')}
                      value={formData.prior_auth_number}
                      onChange={(e) => setFormData({ ...formData, prior_auth_number: e.target.value })}
                      className="flex-1"
                    />
                  )}
                </div>
              </div>
            </div>

            {/* Notes */}
            <div>
              <Label htmlFor="notes">{t('serviceReferrals.billing.notes', 'Notes')}</Label>
              <Textarea
                id="notes"
                value={formData.notes || ''}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder={t('serviceReferrals.billing.additionalNotes', 'Additional billing notes...')}
                rows={2}
              />
            </div>

            {/* Submit Button */}
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setShowAddForm(false)}>
                {t('common.cancel', 'Cancel')}
              </Button>
              <Button type="submit" disabled={saving}>
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {t('serviceReferrals.billing.createBillingRecord', 'Create Billing Record')}
              </Button>
            </div>
          </form>
        )}

        {/* Loading State */}
        {loading && (
          <div className="py-8 flex justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
          </div>
        )}

        {/* Empty State */}
        {!loading && billingRecords.length === 0 && (
          <div className="py-8 text-center text-gray-500">
            <FileText className="w-8 h-8 mx-auto mb-2 text-gray-300" />
            <p>{t('serviceReferrals.billing.noBillingRecordsYet', 'No billing records yet')}</p>
            {!readOnly && (
              <Button variant="link" onClick={() => setShowAddForm(true)}>
                {t('serviceReferrals.billing.createFirstBillingRecord', 'Create first billing record')}
              </Button>
            )}
          </div>
        )}

        {/* Billing Records List */}
        {!loading && billingRecords.length > 0 && (
          <div className="space-y-3">
            {billingRecords.map((record) => {
              const isExpanded = expandedRecordId === record.id;

              return (
                <div key={record.id} className="border rounded-lg overflow-hidden">
                  {/* Header Row */}
                  <div
                    className="px-4 py-3 flex items-center justify-between cursor-pointer hover:bg-gray-50"
                    onClick={() => setExpandedRecordId(isExpanded ? null : record.id)}
                  >
                    <div className="flex items-center gap-3">
                      <Badge variant="outline" className="font-mono">
                        {record.billing_code}
                      </Badge>
                      <Badge variant="outline" className="text-xs">
                        {record.code_system}
                      </Badge>
                      {renderStatusBadge(record.billing_status)}
                      <div className="flex items-center gap-1 text-sm text-gray-500">
                        <Calendar className="w-3 h-3" />
                        {formatDate(record.service_date)}
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <div className="font-medium">{formatCurrency(record.total_amount)}</div>
                        {record.amount_paid > 0 && (
                          <div className="text-xs text-green-600">
                            {t('serviceReferrals.billing.paid', 'Paid')}: {formatCurrency(record.amount_paid)}
                          </div>
                        )}
                      </div>
                      {isExpanded ? (
                        <ChevronUp className="w-4 h-4 text-gray-400" />
                      ) : (
                        <ChevronDown className="w-4 h-4 text-gray-400" />
                      )}
                    </div>
                  </div>

                  {/* Expanded Content */}
                  {isExpanded && (
                    <div className="px-4 py-3 border-t bg-gray-50 space-y-3">
                      <div className="grid grid-cols-3 gap-4 text-sm">
                        <div>
                          <span className="text-gray-500">{t('serviceReferrals.billing.units', 'Units')}:</span>{' '}
                          <span className="font-medium">{record.units}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">{t('serviceReferrals.billing.rate', 'Rate')}:</span>{' '}
                          <span className="font-medium">{formatCurrency(record.rate_per_unit)}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">{t('serviceReferrals.billing.placeOfService', 'Place of Service')}:</span>{' '}
                          <span className="font-medium">{record.place_of_service || '-'}</span>
                        </div>
                      </div>

                      {record.prior_auth_required && (
                        <div className="flex items-center gap-2 text-sm">
                          <AlertCircle className="w-4 h-4 text-amber-500" />
                          <span className="text-gray-500">{t('serviceReferrals.billing.priorAuth', 'Prior Auth')}:</span>
                          <span className="font-medium">{record.prior_auth_number || t('serviceReferrals.billing.required', 'Required')}</span>
                        </div>
                      )}

                      {record.modifier_codes && record.modifier_codes.length > 0 && (
                        <div className="text-sm">
                          <span className="text-gray-500">{t('serviceReferrals.billing.modifiers', 'Modifiers')}:</span>{' '}
                          {record.modifier_codes.map((m, i) => (
                            <Badge key={i} variant="outline" className="ml-1 text-xs">{m}</Badge>
                          ))}
                        </div>
                      )}

                      {record.diagnosis_codes && record.diagnosis_codes.length > 0 && (
                        <div className="text-sm">
                          <span className="text-gray-500">{t('serviceReferrals.billing.diagnosis', 'Diagnosis')}:</span>{' '}
                          {record.diagnosis_codes.map((d, i) => (
                            <Badge key={i} variant="outline" className="ml-1 text-xs">{d}</Badge>
                          ))}
                        </div>
                      )}

                      {record.submitted_at && (
                        <div className="text-sm">
                          <span className="text-gray-500">{t('serviceReferrals.billing.submitted', 'Submitted')}:</span>{' '}
                          <span>{formatDate(record.submitted_at)}</span>
                          {record.submission_reference && (
                            <span className="ml-2 font-mono text-xs">
                              Ref: {record.submission_reference}
                            </span>
                          )}
                        </div>
                      )}

                      {record.denial_reason && (
                        <div className="p-2 bg-red-50 rounded text-sm text-red-700">
                          <strong>{t('serviceReferrals.billing.denialReason', 'Denial Reason')}:</strong> {record.denial_reason}
                          {record.denial_code && (
                            <Badge variant="outline" className="ml-2 text-xs">{record.denial_code}</Badge>
                          )}
                        </div>
                      )}

                      {record.notes && (
                        <div className="text-sm">
                          <span className="text-gray-500">{t('serviceReferrals.billing.notes', 'Notes')}:</span>{' '}
                          <span>{record.notes}</span>
                        </div>
                      )}

                      <div className="text-xs text-gray-400 pt-2 border-t">
                        {t('serviceReferrals.billing.created', 'Created')} {fmt.dateTime(record.created_at)}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default BillingIntegration;
